import { create } from "zustand";
import { onboardingApi } from "../services/onboarding-api";
import { CHECKLIST_ITEMS } from "../types";

interface OnboardingState {
	completedSteps: string[];
	lastCompletedStep: string | null;
	loading: boolean;
	popoverOpen: boolean;
	fetch: (workspaceId: string) => Promise<void>;
	completeStep: (workspaceId: string, step: string) => Promise<void>;
	dismiss: (workspaceId: string) => void;
	isDismissed: (workspaceId: string) => boolean;
	isComplete: (step: string) => boolean;
	primaryCompleted: () => number;
	setPopoverOpen: (open: boolean) => void;
}

const DISMISS_KEY = (id: string) => `onboarding_dismissed_${id}`;

export const useOnboardingStore = create<OnboardingState>((set, get) => ({
	completedSteps: [],
	lastCompletedStep: null,
	loading: false,
	popoverOpen: false,

	fetch: async (workspaceId: string) => {
		set({ loading: true });
		const result = await onboardingApi.get(workspaceId);
		if (result.success && result.data) {
			set({ completedSteps: result.data.map((r) => r.step) });
		}
		set({ loading: false });
	},

	completeStep: async (workspaceId: string, step: string) => {
		if (get().completedSteps.includes(step)) return;
		// Optimistic update
		set((state) => ({ completedSteps: [...state.completedSteps, step], lastCompletedStep: step }));
		const result = await onboardingApi.complete(workspaceId, step);
		if (!result.success) {
			// Rollback
			set((state) => ({ completedSteps: state.completedSteps.filter((s) => s !== step), lastCompletedStep: null }));
		} else {
			// Clear after animation window
			setTimeout(() => set({ lastCompletedStep: null }), 2000);
		}
	},

	dismiss: (workspaceId: string) => {
		localStorage.setItem(DISMISS_KEY(workspaceId), "true");
		set({ popoverOpen: false });
	},

	isDismissed: (workspaceId: string) => {
		return localStorage.getItem(DISMISS_KEY(workspaceId)) === "true";
	},

	isComplete: (step: string) => {
		return get().completedSteps.includes(step);
	},

	primaryCompleted: () => {
		const completed = get().completedSteps;
		return CHECKLIST_ITEMS.filter((item) => completed.includes(item.key)).length;
	},

	setPopoverOpen: (open: boolean) => set({ popoverOpen: open }),
}));
